/**
 * Decision Detail (Decisions v4, M7–M12 / D4–D9) — the open decision's card
 * with Assign, the rationale-gated Resolve, the optional report flags and the
 * two-step client-report sheet; and the read-only Resolved record shown once
 * a decision is resolved.
 *
 * Judgment rules carried over unchanged from the v3 detail:
 *  - Responsible party starts blank. The pick list only offers names already
 *    used on this project, A–Z, and nothing is pre-selected (rule §4.2).
 *  - Resolve needs a typed rationale; no default text is ever supplied.
 *  - Report flags default off. The client flag is set only by the second
 *    confirmation (rule §4.7 / AC-1g-17), and while CLIENT_REPORT_ENABLED is
 *    false a tap explains itself through the shell's coming-soon modal.
 *
 * Mutations are honest about where they are. Assign and Resolve await the
 * app's outbox save (ordinal allocation → IndexedDB → release); while one is in
 * flight every other mutation control is locked, because both routes spend
 * from the same per-decision ordinal cursor. A local failure is shown with a
 * Retry. A change that saved but has not been delivered is labelled queued —
 * only a delivered change, or the server's own read, is shown as done.
 */
import { useState } from "preact/hooks";
import { ageDays, type Decision } from "../../slice/decision-slice";
import { initials } from "../../slice/decision-query";
import type { DecisionMutations } from "../../read/reconcile";
import { CLIENT_REPORT_COMING_SOON, CLIENT_REPORT_ENABLED } from "../../triage/client-report-gate";
import { BackLink } from "../BackLink";
import { Chip } from "../Chip";
import { DecisionSheet } from "./DecisionSheet";
import { CheckIcon, ChevronDownIcon, InfoIcon } from "./icons";

export interface ReportFlags {
  daily: boolean;
  weekly: boolean;
  client: boolean;
}

const DAY_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function dayLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : DAY_FMT.format(d);
}

function timeLabel(when: Date | string): string {
  const d = typeof when === "string" ? new Date(when) : when;
  return Number.isNaN(d.getTime()) ? "" : TIME_FMT.format(d);
}

function daysText(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

const OTHER_PERSON = "__other__";
const LOCAL_SAVE_FAILED = "Couldn’t save this on the phone, so nothing was sent.";

function Fields({ decision }: { decision: Decision }) {
  const author = decision.author_name || decision.author;
  if (!decision.room_zone && !decision.decides && !decision.trade && !author) return null;
  return (
    <div class="decision-detail-fields">
      {decision.room_zone && <p data-testid="decision-detail-room">Room: {decision.room_zone}</p>}
      {decision.decides && <p data-testid="decision-detail-decides">Decision maker: {decision.decides}</p>}
      {decision.trade && <p data-testid="decision-detail-trade">Trade: {decision.trade}</p>}
      {author && <p data-testid="decision-detail-author">Filed by {author}</p>}
    </div>
  );
}

function Person({ name }: { name: string }) {
  return (
    <span class="decision-person" data-testid="decision-detail-owner">
      <span class="decision-avatar" aria-hidden="true">
        {initials(name)}
      </span>
      <span class="decision-person-name">{name}</span>
    </span>
  );
}

function Activity({ lines }: { lines: string[] }) {
  return (
    <div class="decision-detail-section" data-testid="decision-detail-activity">
      <p class="decision-eyebrow">Activity</p>
      <div class="decision-activity">
        {lines.map((line, i) => (
          <span key={i}>{line}</span>
        ))}
      </div>
    </div>
  );
}

function Failure({
  message,
  onRetry,
  busy,
  testid,
}: {
  message: string;
  onRetry: () => void;
  busy: boolean;
  testid: string;
}) {
  return (
    <div class="decision-failure" role="alert" data-testid={testid}>
      <p>{message}</p>
      <button
        type="button"
        class="decision-link-button"
        data-testid={`${testid}-retry`}
        disabled={busy}
        onClick={onRetry}
      >
        Try again
      </button>
    </div>
  );
}

// ---------- open decision ----------

/** What a resolve just queued from this screen — handed up so the Resolved
 * record shows what the human actually submitted, including an owner that is
 * itself still queued. */
export interface SubmittedResolve {
  rationale: string;
  reports: ReportFlags;
  owner?: string;
  queuedAt: Date;
}

export interface DecisionDetailProps {
  /** The decision as the latest read (plus in-flight local changes) has it,
   * or null if it has dropped out of the read. */
  decision: Decision | null;
  /** Capture id of the opened decision — what every callback carries. */
  decisionRef: string;
  people: string[];
  /** This decision's latest assign/resolve changes in the device outbox. */
  mutations?: DecisionMutations;
  onBack: () => void;
  /** Resolve once the change is durably queued; reject if it could not be. */
  onAssignOwner: (decision_ref: string, owner: string) => Promise<void> | void;
  onResolve: (decision_ref: string, rationale: string, reports: ReportFlags) => Promise<void> | void;
  /** Called after a resolve has been durably queued. */
  onResolveQueued: (submitted: SubmittedResolve) => void;
  onComingSoon?: (message: string) => void;
}

type Busy = "assign" | "resolve" | null;

export function DecisionDetail(props: DecisionDetailProps) {
  const { decision, decisionRef, people, mutations } = props;
  const [choice, setChoice] = useState("");
  const [typedName, setTypedName] = useState("");
  const [changingOwner, setChangingOwner] = useState(false);
  // An assignment queued from this screen, until the outbox/read can speak for it.
  const [queuedOwner, setQueuedOwner] = useState<{ name: string; at: Date } | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [failure, setFailure] = useState<{ op: "assign" | "resolve"; retry: () => void } | null>(null);
  const [rationale, setRationale] = useState("");
  const [daily, setDaily] = useState(false);
  const [weekly, setWeekly] = useState(false);
  const [client, setClient] = useState(false);
  const [clientStep, setClientStep] = useState<0 | 1 | 2>(0);

  const locked = busy !== null;
  const assignMut = mutations?.assign;
  // The latest assignment that has not been delivered yet, from the outbox when
  // the app supplies it, else from what this screen queued.
  const pendingAssign: { name: string; state: "pending" | "rejected"; at: string | Date } | null =
    assignMut && assignMut.state !== "delivered" && assignMut.owner
      ? { name: assignMut.owner, state: assignMut.state, at: assignMut.createdAt }
      : !assignMut && queuedOwner && decision?.owner !== queuedOwner.name
        ? { name: queuedOwner.name, state: "pending", at: queuedOwner.at }
        : null;
  // A queued owner is what a resolve would land with; a refused one is not.
  const owner = pendingAssign?.state === "pending" ? pendingAssign.name : decision?.owner;
  const showPicker = !owner || changingOwner;
  const typing = people.length === 0 || choice === OTHER_PERSON;
  const pickedName = (typing ? typedName : choice).trim();
  const rationaleReady = rationale.trim().length > 0;
  const title = decision?.text ?? decisionRef;

  async function assign(name: string) {
    if (!name || busy) return; // a person must be chosen — nothing is assumed
    setBusy("assign");
    setFailure(null);
    try {
      await props.onAssignOwner(decisionRef, name);
      setQueuedOwner({ name, at: new Date() });
      setChangingOwner(false);
      setChoice("");
      setTypedName("");
      setBusy(null);
    } catch {
      setBusy(null);
      setFailure({ op: "assign", retry: () => void assign(name) });
    }
  }

  async function resolve() {
    const trimmed = rationale.trim();
    if (!trimmed || busy) return; // rationale REQUIRED — no default text supplied
    const reports = { daily, weekly, client };
    setBusy("resolve");
    setFailure(null);
    try {
      await props.onResolve(decisionRef, trimmed, reports);
      setBusy(null);
      props.onResolveQueued({ rationale: trimmed, reports, owner, queuedAt: new Date() });
    } catch {
      setBusy(null);
      setFailure({ op: "resolve", retry: () => void resolve() });
    }
  }

  function tapClient() {
    if (locked) return;
    if (!CLIENT_REPORT_ENABLED) {
      props.onComingSoon?.(CLIENT_REPORT_COMING_SOON);
      return;
    }
    if (client) {
      // Withdrawing an affirmed flag is one tap — the confirmation guards opting IN.
      setClient(false);
      return;
    }
    setClientStep(1);
  }

  const activity: string[] = [];
  if (pendingAssign) {
    activity.push(
      `${pendingAssign.state === "pending" ? "Assignment queued" : "Assignment refused"}: ${pendingAssign.name} · ${timeLabel(pendingAssign.at)}`,
    );
  } else if (assignMut?.state === "delivered" && assignMut.owner) {
    activity.push(`Assigned to ${assignMut.owner} · ${timeLabel(assignMut.deliveredAt ?? assignMut.createdAt)}`);
  }
  if (mutations?.resolve?.state === "rejected") {
    activity.push(`Resolve refused · ${timeLabel(mutations.resolve.createdAt)}`);
  }
  if (decision) activity.push(`Raised ${dayLabel(decision.first_raised_ts)}`);

  return (
    <>
      <BackLink label="Decisions" onBack={props.onBack} testid="decision-detail-close" />
      <div class="decision-card decision-detail-grid" data-testid="decision-detail" aria-busy={locked}>
        <div class="decision-detail-col">
          <div class="decision-detail-head" style="order:1">
            <h2 class="decision-detail-title" data-testid="decision-detail-title">
              {title}
            </h2>
            {decision && (
              <p class="decision-detail-age" data-testid="decision-detail-age">
                {`Open ${daysText(ageDays(decision))} · Raised ${dayLabel(decision.first_raised_ts)}`}
              </p>
            )}
            {decision && <Fields decision={decision} />}
          </div>

          <div class="decision-detail-section" style="order:2" data-testid="decision-owner">
            <p class="decision-eyebrow">Assign responsible party</p>
            {!showPicker && owner ? (
              <>
                <div class="decision-person-row">
                  <Person name={owner} />
                  <button
                    type="button"
                    class="decision-quiet-button"
                    data-testid="decision-owner-change"
                    disabled={locked}
                    onClick={() => setChangingOwner(true)}
                  >
                    Change
                  </button>
                </div>
                {pendingAssign?.state === "pending" && (
                  <p class="decision-hint decision-hint--pending" data-testid="decision-owner-pending">
                    Queued on this phone. Not synced yet.
                  </p>
                )}
              </>
            ) : (
              <>
                {people.length > 0 && (
                  <label class="decision-select">
                    <span class="decision-visually-hidden">Responsible party</span>
                    <select
                      data-testid="decision-owner-select"
                      value={choice}
                      disabled={locked}
                      onChange={(e) => setChoice((e.currentTarget as HTMLSelectElement).value)}
                    >
                      <option value="">Choose a person</option>
                      {people.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                      <option value={OTHER_PERSON}>Someone else…</option>
                    </select>
                    <ChevronDownIcon />
                  </label>
                )}
                {typing && (
                  <input
                    class="decision-text-input"
                    data-testid="decision-owner-input"
                    aria-label="Responsible party name"
                    placeholder="Type a name"
                    value={typedName}
                    disabled={locked}
                    onInput={(e) => setTypedName((e.currentTarget as HTMLInputElement).value)}
                  />
                )}
                <button
                  type="button"
                  class="decision-outline-button"
                  data-testid="decision-owner-submit"
                  disabled={!pickedName || locked}
                  onClick={() => void assign(pickedName)}
                >
                  {busy === "assign" ? "Saving…" : "Assign"}
                </button>
                {!owner && <p class="decision-hint">Stays unassigned until you choose a person.</p>}
              </>
            )}
            {failure?.op === "assign" && (
              <Failure
                message={LOCAL_SAVE_FAILED}
                onRetry={failure.retry}
                busy={locked}
                testid="decision-owner-failure"
              />
            )}
            {!failure && pendingAssign?.state === "rejected" && (
              <Failure
                message={`The server didn’t accept assigning ${pendingAssign.name}.`}
                onRetry={() => void assign(pendingAssign.name)}
                busy={locked}
                testid="decision-owner-rejected"
              />
            )}
          </div>

          <div style="order:4">
            <Activity lines={activity} />
          </div>
        </div>

        <div class="decision-detail-col">
          <div class="decision-detail-section" style="order:3">
            <label class="decision-eyebrow" for={`decision-rationale-${decisionRef}`}>
              Rationale (required to resolve)
            </label>
            <textarea
              id={`decision-rationale-${decisionRef}`}
              class="decision-rationale"
              data-testid="decision-rationale-input"
              placeholder="Why is this decided? Add a short rationale before this can resolve."
              value={rationale}
              disabled={busy === "resolve"}
              onInput={(e) => setRationale((e.currentTarget as HTMLTextAreaElement).value)}
            />
            {rationaleReady && (
              <p class="decision-hint decision-hint--done" data-testid="decision-rationale-added">
                <CheckIcon size={14} /> Rationale added
              </p>
            )}
          </div>

          <div class="decision-detail-section" style="order:5" data-testid="decision-report-chips">
            <p class="decision-eyebrow">Add resolution to a report (optional)</p>
            <div class="decision-report-grid">
              <Chip
                class="decision-report-chip"
                data-testid="decision-report-daily"
                aria-pressed={daily}
                disabled={locked}
                onClick={() => setDaily((v) => !v)}
              >
                {daily && <CheckIcon />}
                Daily report
              </Chip>
              <Chip
                class="decision-report-chip"
                data-testid="decision-report-weekly"
                aria-pressed={weekly}
                disabled={locked}
                onClick={() => setWeekly((v) => !v)}
              >
                {weekly && <CheckIcon />}
                Weekly report
              </Chip>
              <Chip
                class="decision-report-chip decision-report-chip--wide"
                data-testid="decision-report-client"
                aria-pressed={client}
                aria-disabled={!CLIENT_REPORT_ENABLED || locked}
                onClick={tapClient}
              >
                {client && <CheckIcon />}
                Client report
              </Chip>
            </div>
            <p class="decision-hint">Client report needs a second confirmation before anything is sent.</p>
          </div>

          <div class="decision-detail-section" style="order:6">
            {mutations?.resolve?.state === "rejected" && !failure && (
              <p class="decision-failure-text" data-testid="decision-resolve-rejected">
                The server didn’t accept the last resolve, so this decision is still open.
              </p>
            )}
            {!rationaleReady && (
              <p class="decision-hint decision-hint--info" data-testid="decision-resolve-hint">
                <InfoIcon size={14} /> Add a rationale to resolve.
              </p>
            )}
            {busy === "assign" && rationaleReady && (
              <p class="decision-hint" data-testid="decision-resolve-wait">
                Saving the assignment first…
              </p>
            )}
            <button
              type="button"
              data-testid="decision-resolve-submit"
              disabled={!rationaleReady || locked}
              onClick={() => void resolve()}
            >
              {busy === "resolve" ? "Saving…" : "Resolve"}
            </button>
            {failure?.op === "resolve" && (
              <Failure
                message={LOCAL_SAVE_FAILED}
                onRetry={failure.retry}
                busy={locked}
                testid="decision-resolve-failure"
              />
            )}
          </div>
        </div>
      </div>

      {clientStep !== 0 && (
        <DecisionSheet
          variant="dialog"
          label="Client report"
          testid="decision-client-confirm"
          onClose={() => setClientStep(0)}
        >
          {clientStep === 1 ? (
            <>
              <p class="decision-eyebrow">Client report · Step 1 of 2</p>
              <h3 class="decision-sheet-title">Add this decision to the client report?</h3>
              <p class="decision-sheet-body">
                Clients can see everything in this report. You will be asked to confirm once more before anything is
                sent.
              </p>
              <div class="decision-sheet-actions">
                <button
                  type="button"
                  class="decision-primary-button"
                  data-testid="decision-client-confirm-continue"
                  onClick={() => setClientStep(2)}
                >
                  Continue
                </button>
                <button
                  type="button"
                  class="decision-outline-button"
                  data-testid="decision-client-confirm-cancel"
                  onClick={() => setClientStep(0)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p class="decision-eyebrow">Client report · Step 2 of 2</p>
              <h3 class="decision-sheet-title">Confirm: this goes to the client</h3>
              <p class="decision-sheet-body">“{title}” and its rationale will be included in the next client report.</p>
              <div class="decision-sheet-actions">
                <button
                  type="button"
                  class="decision-primary-button"
                  data-testid="decision-client-confirm-yes"
                  onClick={() => {
                    setClient(true);
                    setClientStep(0);
                  }}
                >
                  Confirm and add
                </button>
                <button
                  type="button"
                  class="decision-outline-button"
                  data-testid="decision-client-confirm-back"
                  onClick={() => setClientStep(1)}
                >
                  Go back
                </button>
              </div>
            </>
          )}
        </DecisionSheet>
      )}
    </>
  );
}

// ---------- resolved record ----------

/** `queued`: saved on the phone, not yet delivered. `rejected`: the server
 * refused it. `resolved`: delivered, or resolved in the server's read. */
export type ResolveStatus = "queued" | "rejected" | "resolved";

export interface ResolvedRecord {
  decision: Decision;
  status: ResolveStatus;
  rationale?: string;
  /** When it was resolved (status `resolved`), or queued (status `queued`). */
  at?: Date;
  owner?: string;
  /** Only known for a resolve made on this screen. */
  reports?: ReportFlags;
}

export interface DecisionResolvedViewProps {
  record: ResolvedRecord;
  onBack: () => void;
  /** Re-send a refused or failed resolve with the same rationale and reports. */
  onRetry?: () => void;
  retryBusy?: boolean;
  retryFailed?: boolean;
}

export function DecisionResolvedView({ record, onBack, onRetry, retryBusy, retryFailed }: DecisionResolvedViewProps) {
  const { decision, status, rationale, at, owner, reports } = record;
  const raised = new Date(decision.first_raised_ts);
  const openDays =
    status === "resolved" && at && !Number.isNaN(raised.getTime())
      ? Math.max(0, Math.floor((at.getTime() - raised.getTime()) / (24 * 60 * 60 * 1000)))
      : null;
  const when = at ? timeLabel(at) : "";
  const headline =
    status === "resolved"
      ? when
        ? `Resolved ${when}`
        : "Resolved"
      : status === "queued"
        ? `Resolve queued${when ? ` ${when}` : ""}`
        : "Resolve refused";
  const meta = [
    headline,
    openDays !== null ? `Open ${daysText(openDays)}` : null,
    `Raised ${dayLabel(decision.first_raised_ts)}`,
  ].filter(Boolean);
  const activity = [
    status === "resolved"
      ? `Resolved${when ? ` · ${when}` : ""}`
      : status === "queued"
        ? `Resolve queued${when ? ` · ${when}` : ""}`
        : "Resolve refused by the server",
    `Raised ${dayLabel(decision.first_raised_ts)}`,
  ];
  const added = reports
    ? [reports.daily && "Daily report", reports.weekly && "Weekly report", reports.client && "Client report"].filter(
        (r): r is string => !!r,
      )
    : [];

  return (
    <>
      <BackLink label="Decisions" onBack={onBack} testid="decision-detail-close" />
      <div class="decision-card decision-detail-grid" data-testid="decision-resolved" data-status={status}>
        <div class="decision-detail-col">
          <div class="decision-detail-head" style="order:1">
            <p
              class={`decision-resolved-badge decision-resolved-badge--${status}`}
              data-testid="decision-resolved-status"
            >
              {status === "resolved" && (
                <>
                  <CheckIcon /> Resolved
                </>
              )}
              {status === "queued" && "Queued on this phone · not synced yet"}
              {status === "rejected" && "Not resolved · the server didn’t accept it"}
            </p>
            <h2 class="decision-detail-title" data-testid="decision-detail-title">
              {decision.text}
            </h2>
            <p class="decision-detail-age" data-testid="decision-detail-age">
              {meta.join(" · ")}
            </p>
            <Fields decision={decision} />
          </div>
          <div class="decision-detail-section" style="order:2">
            <p class="decision-eyebrow">Responsible party</p>
            {owner ? <Person name={owner} /> : <p class="decision-muted">Unassigned</p>}
          </div>
          <div style="order:4">
            <Activity lines={activity} />
          </div>
        </div>
        <div class="decision-detail-col">
          {rationale && (
            <div class="decision-detail-section" style="order:3">
              <p class="decision-eyebrow">Rationale</p>
              <p class="decision-resolved-text" data-testid="decision-resolved-rationale">
                {rationale}
              </p>
            </div>
          )}
          {added.length > 0 && (
            <div class="decision-detail-section" style="order:5">
              <p class="decision-eyebrow">{status === "resolved" ? "Added to report" : "To be added to report"}</p>
              <p class="decision-resolved-text" data-testid="decision-resolved-reports">
                {added.join(", ")}
              </p>
            </div>
          )}
          {status === "rejected" && onRetry && (
            <div class="decision-detail-section" style="order:6">
              {retryFailed && (
                <p class="decision-failure-text" role="alert" data-testid="decision-resolve-failure">
                  {LOCAL_SAVE_FAILED}
                </p>
              )}
              <button
                type="button"
                class="decision-primary-button"
                data-testid="decision-resolve-retry"
                disabled={retryBusy}
                onClick={onRetry}
              >
                {retryBusy ? "Saving…" : "Try again"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
